// External Libraries
import * as React from "react";
import { observer } from "mobx-react";
import { useLocalization } from "@shared-ui/localization-context";
import { useBexApi } from "@shared-ui/bex-api-context";

// UITK
import { UitkHeading, UitkText } from "uitk-react-text";
import { UitkSecondaryButton } from "uitk-react-button";
import { UitkSpacing } from "uitk-react-spacing";

// Components
import { HotelSearchLinkBuilder } from "components/utility/HotelSearchLinkBuilder/HotelSearchLinkBuilder";
import { FlexClickTracker } from "components/utility/analytics/FlexClickTracker/FlexClickTracker";
import { SchemaOrgScript } from "components/utility/SchemaOrg/SchemaOrgScript";
import { hotelStructuredData } from "components/flexComponents/Hotels/utils/getHotelStructuredData";
import { hotelsViewTypes, topLevelViewClassName } from "./HotelUtils";
import { withStores } from "stores";
import { HotelsContext } from "../../components/HotelsContext";
import { useMapPropertySearchByIdQuery } from "components/flexComponents/Hotels/hooks/useMapPropertySearchByIdQuery";

// Types
import { HotelsFlexModuleResult } from "typings/microserviceModels/hotels-flex-module";
import { HotelsViewsProps, HotelsViewsQueryComponentProps } from "components/flexComponents/Hotels/typings";

const ButtonTracked = FlexClickTracker(UitkSecondaryButton);

const HotelsViews = withStores(
  "hotels",
  "flexModuleModelStore"
)(
  observer((props: HotelsViewsProps) => {
    const { templateComponent, flexModuleModelStore, context, hotels } = props;

    if (!templateComponent) {
      return null;
    }
    const {
      metadata: { id },
    } = templateComponent;
    const model = flexModuleModelStore.getModel(id) as HotelsFlexModuleResult | null;

    if (!model || !model.hotels?.length) {
      return null;
    }

    return (
      <HotelsViewsQueryComponent
        templateComponent={templateComponent}
        context={context}
        hotels={hotels}
        model={model}
      />
    );
  })
);

const HotelsViewsQueryComponent = (props: HotelsViewsQueryComponentProps) => {
  const { templateComponent, context, model, hotels: hotelsStore } = props;

  if (!templateComponent) {
    return null;
  }
  const { context: bexApiContext } = useBexApi();
  const { formatText } = useLocalization();
  const [hasSetHotelsOnStore, setHasSetHotelsOnStore] = React.useState(false);
  const showHotelsOnMap = typeof model.showMarkersOnMap === "undefined" || model.showMarkersOnMap;
  const locationId = model.destinationId ?? context.searchContext.location?.id;
  const shouldSkipQuery = (!(model.hotels.length > 0) && !showHotelsOnMap) || hasSetHotelsOnStore;

  const { data, error } = useMapPropertySearchByIdQuery(
    model.hotels.map((hotel) => hotel.hotelId),
    hotelsStore.getSearchDateRange(),
    bexApiContext,
    shouldSkipQuery
  );

  if (data) {
    hotelsStore.setAllHotelsOnMapOnSuccessResponse(data.propertySearch.properties, locationId);

    setHasSetHotelsOnStore(true);
  }

  if (error) {
    hotelsStore.setAllHotelsOnMapOnFailureResponse(model.hotels, context, locationId);
  }

  const {
    metadata: { id },
  } = templateComponent;
  const { view, hotels, seeAllLink, seeAllType, locationName, showLegalText } = model;

  const View = hotelsViewTypes[view];

  return (
    <HotelsContext.Provider value={{ context, templateComponent, model }}>
      <SchemaOrgScript structuredData={hotelStructuredData(hotels)} />
      <UitkSpacing margin={{ blockstart: "three" }}>
        <div id={id} className={`Hotels ${topLevelViewClassName[view]}`}>
          {Boolean(model.title) && (
            <UitkHeading tag="h2" size={4}>
              {model.title}
            </UitkHeading>
          )}

          <View context={context} model={model} />

          {Boolean(seeAllLink) && (
            <UitkText align="center" size={300} spacing="six">
              <UitkText weight="bold" className="hotelRating">
                {formatText("hotels.lookingFor")}
              </UitkText>
              <UitkText>{formatText("hotels.seeAllAvailableProperties", locationName)}</UitkText>
              <UitkSpacing margin={{ block: "three" }}>
                <ButtonTracked
                  moduleName="hotels"
                  rfrr={seeAllType}
                  rel="nofollow"
                  href={new HotelSearchLinkBuilder(seeAllLink).build(context)}
                  linkName={seeAllLink}
                >
                  {formatText("hotels.viewAllProperties")}
                </ButtonTracked>
              </UitkSpacing>
            </UitkText>
          )}

          {showLegalText && (
            <UitkSpacing padding={{ blockstart: "three" }}>
              <UitkText size={200} align="center" className="legalText">
                {formatText("hotels.legal.disclaimer")}
              </UitkText>
            </UitkSpacing>
          )}
        </div>
      </UitkSpacing>
    </HotelsContext.Provider>
  );
};

HotelsViews.displayName = "HotelsViews";

export default HotelsViews;
